import { DEFAULT_ACTION_PRIORITY } from 'components/IAP/ICSConstants';
import type { ActionChecklistItem } from 'components/IAP/actionChecklistModel';
import { normalizeChecklistItems } from 'components/IAP/actionChecklistModel';
import {
  normalizeAttachments,
  normalizeHazards,
  normalizeRequirements,
} from 'components/IAP/actionTicket/actionTicketModel';
import type {
  OrgEventChecklistItemDef,
  OrgEventChecklistOrgActionTemplate,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import type {
  PlanLibraryPriority,
  PlanLibraryTask,
} from 'hooks/planLibrary/planLibraryHooks';
import { emptyPlanLibraryTask } from '../PlanLibraryTab/planLibraryTaskModel';

function asIso(value: Date | string | null | undefined): string | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  return String(value);
}

function asPriority(value: string | undefined): PlanLibraryPriority {
  if (
    value === 'Low' ||
    value === 'Normal' ||
    value === 'High' ||
    value === 'Critical'
  ) {
    return value;
  }
  return DEFAULT_ACTION_PRIORITY as PlanLibraryPriority;
}

export function checklistItemToPlanTask(
  item: OrgEventChecklistItemDef
): PlanLibraryTask {
  const template = item.orgAction ?? {};
  return {
    ...emptyPlanLibraryTask(),
    id: item.id,
    name: item.label,
    description: item.description ?? '',
    status: template.status || 'Planned',
    priority: asPriority(template.priority),
    checklist: normalizeChecklistItems(template.checklist),
    assignee_kind: template.assigneeKind ?? null,
    assignee_id: template.assigneeId ?? null,
    poc: template.poc ?? null,
    briefed_poc: Boolean(template.briefedPoc),
    start_date: template.startDate ?? null,
    target_date: template.targetDate ?? null,
    geolocation: template.geolocation ?? null,
    symbology: template.symbology ?? null,
    aor_id: template.aorId ?? template.aorIds?.[0] ?? null,
    aor_ids: template.aorIds?.length
      ? template.aorIds
      : template.aorId
        ? [template.aorId]
        : [],
    reporting_location: template.reportingLocation ?? null,
    requested_arrival_time: template.requestedArrivalTime ?? null,
    resource_requirements: normalizeRequirements(template.resourceRequirements),
    resource_requirements_na: Boolean(template.resourceRequirementsNa),
    hazards: normalizeHazards(template.hazards),
    attachments: normalizeAttachments(template.attachments),
    depends_on: template.dependsOn ?? [],
  };
}

export function planTaskToChecklistItem(
  task: Omit<PlanLibraryTask, 'id'> & { id?: string },
  existing: OrgEventChecklistItemDef
): OrgEventChecklistItemDef {
  const checklist = (task.checklist ?? []).filter((row) => row.label.trim());
  const orgAction: OrgEventChecklistOrgActionTemplate = {
    status: task.status || 'Planned',
    poc: task.poc ?? null,
    briefedPoc: Boolean(task.briefed_poc),
    checklist: checklist.map((row: ActionChecklistItem) => ({
      label: row.label,
      checked: row.checked,
      note: row.note,
      attachments: row.attachments,
      responsible_individual_id: row.responsible_individual_id,
    })),
    priority: task.priority || DEFAULT_ACTION_PRIORITY,
    assigneeKind: task.assignee_kind ?? null,
    assigneeId: task.assignee_id ?? null,
    startDate: asIso(task.start_date),
    targetDate: asIso(task.target_date),
    geolocation: task.geolocation ?? null,
    symbology: task.symbology ?? null,
    aorId: task.aor_ids?.[0] ?? task.aor_id ?? null,
    aorIds: task.aor_ids?.length
      ? task.aor_ids
      : task.aor_id
        ? [task.aor_id]
        : [],
    reportingLocation: task.reporting_location ?? null,
    requestedArrivalTime: asIso(task.requested_arrival_time),
    resourceRequirements: task.resource_requirements ?? [],
    resourceRequirementsNa: Boolean(task.resource_requirements_na),
    hazards: task.hazards ?? [],
    attachments: task.attachments ?? [],
    dependsOn: task.depends_on ?? [],
  };

  return {
    ...existing,
    id: task.id ?? existing.id,
    kind: 'org_action',
    label: task.name.trim(),
    description: task.description?.trim() || undefined,
    orgAction,
    notify: undefined,
  };
}
